import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import Button from "../../../../../../components/button/button.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import {FaCheck, FaCircle, FaEraser, FaSearch, FaUser} from "react-icons/fa";
import {FaXmark} from "react-icons/fa6";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import {MaskDefault} from "../../../../../../utils/mask.js";
import LifeRisk from "../../../citizen/utils/life-risk.js";
import citizenService from "../../../../../../services/web/citizen-service.js";
import protectionProgramService from "../../../../../../services/web/protection-program-service.js";
import {useGlobalKey} from "../../../../../../hooks/use-global-key.js";
import {parse, set} from "date-fns";

const initialFilters = {
    name: "",
    protectionProgram: [],
    lifeRisk: "",
    citizenAddress: "",
    lastActionDt: "",
    cpf: "",
};

const SelectionRow = ({citizen, onRemove}) => {
    const color = LifeRisk.getLifeRiskColor(citizen.lifeRisk);
    const doc = citizen.cpf ? projectUtils.formatCpfToView(citizen.cpf) : projectUtils.formatCnpjToView(citizen.cnpj);
    const addressCount = (citizen.addresses ?? []).length;

    return (
        <div className={"flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-gray-50"}>
            <span className={"shrink-0 w-3 h-3 rounded-full"} style={{backgroundColor: color, boxShadow: `0 0 0 3px ${color}22`}}/>
            <div className={"flex-1 min-w-0"}>
                <div className={"text-[13px] font-semibold text-gray-700 truncate"} title={citizen.name}>{citizen.name}</div>
                <div className={"text-[11px] text-gray-400"}>{doc}{addressCount > 1 ? ` · ${addressCount} endereços` : ""}</div>
            </div>
            <button type="button" title={"Remover"} className={"text-gray-400 hover:text-red-600 cursor-pointer p-1"} onClick={() => onRemove(citizen.id)}>
                <FaXmark/>
            </button>
        </div>
    );
};

const SelectionCard = ({selected, onRemove, onClear, className}) => (
    <div className={["flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden", className].join(" ")}>
        <div className={"flex items-center gap-2 px-4 py-3 border-b border-gray-200"}>
            <FaCheck className={"text-[#063A6C]"}/>
            <span className={"text-sm font-bold text-gray-700"}>Seleção</span>
            <span className={"text-xs font-bold text-white bg-[#063A6C] rounded-full px-2.5 py-0.5"}>{selected.length}</span>
            <div className={"flex-1"}/>
            {
                selected.length > 0 &&
                <button type="button" className={"text-xs font-semibold text-gray-500 hover:text-gray-700 cursor-pointer"} onClick={onClear}>
                    Limpar
                </button>
            }
        </div>

        <div className={"flex-1 overflow-auto p-2"}>
            {
                selected.length === 0
                    ? <div className={"flex flex-col items-center justify-center text-center text-gray-400 h-full gap-2 px-4"}>
                        <FaUser size={32} className={"opacity-50"}/>
                        <span className={"text-xs"}>Selecione os protegidos na tabela</span>
                    </div>
                    : selected.map(citizen => <SelectionRow key={citizen.id} citizen={citizen} onRemove={onRemove}/>)
            }
        </div>
    </div>
);

const ScheduleCitizenSelectionStep = ({type, selected, setSelected}) => {
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const gridRef = useRef(undefined);
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    const buildFilter = (values) => {
        const result = [];

        result.push({field: "name", type: "like", value: values.name ?? ""});
        result.push({field: "protectionProgram", type: "like", value: values.protectionProgram?.map(p => p.value).join(",") ?? ""});
        result.push({field: "lifeRisk", type: "like", value: values.lifeRisk ?? ""});
        result.push({field: "citizenAddress", type: "like", value: values.citizenAddress ?? ""});
        result.push({field: "cpf", type: "like", value: values.cpf?.replace(/[^a-zA-Z0-9]/g, '') ?? ""});

       if (values.lastActionDt) {
           const parsedDate = parse(values.lastActionDt, "dd/MM/yyyy", new Date());

           result.push({
               field: "lastActionDt",
               type: "keywords",
               value: {
                   dtIni: set(parsedDate, {hours: 0, minutes: 0, seconds: 0, milliseconds: 0}).toISOString(),
                   dtEnd: set(parsedDate, {hours: 23, minutes: 59, seconds: 59, milliseconds: 999}).toISOString()
               }
           });
       }

        return result;
    };

    const {data} = useQuery({
        queryFn: () => citizenService.list({filter: [...filter, {field: "addressAction", type: "=", value: type}], sort: [{field: "name", dir: "asc"}]}).then(({data}) => data),
        queryKey: ["schedule-citizen-selection", {type, filter}],
        enabled: !!type,
        placeholderData: keepPreviousData,
        retry: 0
    });

    const {data: programs} = useQuery({
        queryFn: () => protectionProgramService.options(),
        queryKey: ["program"],
    });

    const columnDefs = useMemo(() => [
        {
            field: "lifeRisk", headerName: "Risco", width: 80, sortable: false,
            cellRenderer: ({data: row}) => {
                if (!row?.lifeRisk) return '';

                return (
                    <div className={"flex items-center justify-center h-full w-full"}>
                        <FaCircle color={LifeRisk.getLifeRiskColor(row.lifeRisk)} size={"20px"}/>
                    </div>
                )
            }
        },
        {field: "identificationNumber", headerName: "Nº de identificação", flex: 1, comparator: aggridUtils.nullLastComparator},
        {field: "name", headerName: "Nome do protegido", flex: 1, comparator: aggridUtils.nullLastComparator},
        {
            field: "cpf", headerName: "CPF/CNPJ", flex: 1,
            valueFormatter: ({data: {cpf, cnpj}}) => cpf ? projectUtils.formatCpfToView(cpf) : projectUtils.formatCnpjToView(cnpj)
        },
        {field: "lastActionDt", headerName: type === "VISIT" ? "Última visita" : "Última ronda", flex: 1, comparator: aggridUtils.agGridCompareDateTime},
        {field: "nextScheduleDt", headerName: "Prox. Agendamento", width: 160},
    ], [type]);

    const rowSelection = useMemo(() => ({
        mode: "multiRow",
        headerCheckbox: true,
        enableClickSelection: true,
        enableSelectionWithoutKeys: true,
    }), []);

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const restoreSelection = useCallback(({api}) => {
        const ids = new Set(selectedRef.current.map(c => c.id));
        const toSelect = [];
        const toDeselect = [];

        api.forEachNode(node => (ids.has(node.data.id) ? toSelect : toDeselect).push(node));

        if (toSelect.length) api.setNodesSelected({nodes: toSelect, newValue: true});
        if (toDeselect.length) api.setNodesSelected({nodes: toDeselect, newValue: false});
    }, []);

    useEffect(() => {
        const api = gridRef.current?.api;
        if (api) restoreSelection({api});
    }, [selected, restoreSelection]);

    const removeSelected = (id) => setSelected(prev => prev.filter(c => c.id !== id));

    const clearAll = () => setSelected([]);

    const onSelectionChanged = ({api, source}) => {
        if (source === "api") return;

        const displayedIds = [];
        api.forEachNode(node => displayedIds.push(node.data.id));

        const map = new Map(selectedRef.current.map(c => [c.id, c]));
        displayedIds.forEach(id => map.delete(id));
        api.getSelectedRows().forEach(row => map.set(row.id, row));

        setSelected(Array.from(map.values()));
    };

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => setFilter(buildFilter(filterValues)), [filterValues, type]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter(buildFilter(initialFilters));
    }, [type]);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    if (!type) return null;

    return (
        <Card>
            <Card.Header>
                <Card.Title>Selecionar os protegidos</Card.Title>
            </Card.Header>
            <Card.Body className={"space-y-4"}>
                <div className={"grid grid-cols-12 gap-4"}>
                    <InputForm
                        classNameWrapper={"col-span-3"}
                        id={"name"}
                        label={"Nome do protegido"}
                        value={filterValues.name}
                        onChange={handleInput}
                    />

                    <SelectComponent
                        classNameWrapper={"col-span-3"}
                        value={filterValues.protectionProgram}
                        options={programs ?? []}
                        label={"Programa de proteção"}
                        isMulti
                        onChange={(value) => setFilterValues(prev => ({...prev, protectionProgram: value}))}
                    />

                    <SelectComponent
                        classNameWrapper={"col-span-2"}
                        value={filterValues.lifeRisk}
                        options={LifeRisk.getLifeRisk()}
                        label={"Grau de risco"}
                        onChange={(value) => setFilterValues(prev => ({...prev, lifeRisk: value?.value ?? ""}))}
                    />

                    <InputForm
                        classNameWrapper={"col-span-2 uppercase"}
                        id={"cpf"}
                        label={"CPF/CNPJ"}
                        value={filterValues.cpf}
                        onChange={handleInput}
                        mask={MaskDefault.cpfAndCnpj}
                    />

                    <InputDatetime
                        classNameWrapper={"col-span-2"}
                        id={"lastActionDt"}
                        label={type === "VISIT" ? "Última visita" : "Última ronda"}
                        dateFormat={"L"}
                        returnDateObj={false}
                        selected={filterValues.lastActionDt}
                        onChange={(value) => setFilterValues(prev => ({...prev, lastActionDt: value ?? ""}))}
                    />

                    <div className={"col-span-12 flex justify-between gap-4"}>
                        <InputForm
                            classNameWrapper={"w-full"}
                            id={"citizenAddress"}
                            label={"Endereço"}
                            value={filterValues.citizenAddress}
                            onChange={handleInput}
                        />

                        <div className={"flex gap-2"}>
                            <Button target={"default"} onClick={clear}>
                                <FaEraser/>
                            </Button>
                            <Button onClick={apply}>
                                <FaSearch/>
                            </Button>
                        </div>
                    </div>
                </div>

                <div className={"grid grid-cols-[1fr_320px] gap-4 items-start"}>
                    <div className={"min-h-100 h-[calc(100vh-347px)] aggrid-row-pointer"}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={data?.data ?? []}
                            columnDefs={columnDefs}
                            rowSelection={rowSelection}
                            getRowId={({data: row}) => String(row.id)}
                            pagination
                            paginationPageSize={100}
                            paginationPageSizeSelector={[20, 50, 100, 500]}
                            animateRows={false}
                            suppressCellFocus={true}
                            onGridReady={restoreSelection}
                            onRowDataUpdated={restoreSelection}
                            onSelectionChanged={onSelectionChanged}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={theme}
                        />
                    </div>

                    <SelectionCard
                        selected={selected}
                        onRemove={removeSelected}
                        onClear={clearAll}
                        className={"h-[calc(100vh-347px)]"}
                    />
                </div>
            </Card.Body>
        </Card>
    );
};

export default ScheduleCitizenSelectionStep;
